/**
 * Règle unique d'indexabilité — annuaire & hubs géographiques.
 *
 * POURQUOI CE FICHIER : la règle « cette page mérite-t-elle d'être indexée ? »
 * était dupliquée dans 3 endroits qui divergeaient (sitemap organizations,
 * sitemap cities, page [slug].vue). Un seuil changé à un endroit et pas à
 * l'autre = signal incohérent envoyé à Google (URL dans le sitemap mais
 * noindex sur la page). Source unique désormais.
 *
 * ─── Le seuil est passé de 3 à 1 (audit SEO 2026-08-06) ───────────────────
 * Le seuil « >= 3 items » posé le 2026-07-09 mettait en noindex 31 hubs villes
 * sur 33. Or /associations/ville/lodeve — UNE seule association — a produit
 * 64 impressions et 5 clics en position moyenne 5,9 sur 90 jours (Search
 * Console, sc-domain:donoor.org). On étouffait une page qui performe.
 *
 * Le garde-fou anti-doorway n'est plus un compte brut mais la SUBSTANCE :
 * un hub n'est indexable que s'il liste au moins un item lui-même indexable.
 * Une ville dont l'unique association est une coquille vide reste noindex.
 * Le bon test : « la page contient-elle un fait que l'autre page n'a pas ? »
 * Un hub ville contient la liste réelle des assos de cette ville → oui.
 *
 * ─── Pourquoi des seuils différents par niveau ────────────────────────────
 * Plus le niveau est large, plus il faut d'items pour que la page apporte
 * quelque chose. Une page « région » qui liste 2 assos sur 18 départements
 * n'apporte rien que la page ville n'apporte déjà. Ces seuils décroissent
 * mécaniquement en importance à mesure que la base grandit : à 10 000 assos
 * tous les départements passent, et la règle de non-redondance ci-dessous
 * cesse quasiment de s'appliquer.
 */

/** Hub ville (assos, cagnottes, événements, adhésions) : 1 item réel suffit. */
export const GEO_HUB_MIN_CITY = 1;

/** Hub ville × catégorie : seule branche qui explose en volume (villes × catégories). */
export const GEO_HUB_MIN_CITY_CATEGORY = 3;

/** Hub département. */
export const GEO_HUB_MIN_DEPARTMENT = 3;

/** Hub région. */
export const GEO_HUB_MIN_REGION = 8;

/**
 * Forme minimale d'une organisation pour juger de son indexabilité.
 * Volontairement permissive : ces champs viennent de l'API publique
 * (`/api/organizations/public`) et du payload SSR de la page association.
 */
export interface IndexableOrgShape {
  description?: string | null;
  objet_social?: string | null;
  activities_count?: number | null;
  /** false = ni RNA ni SIRET : fiche « non configurée » (liste publique de l'API). */
  page_configuree?: boolean | null;
}

/**
 * Une fiche d'association publique est-elle indexable ? Oui, toujours
 * (Karim 26/10/2026) : une fiche peu remplie est enrichie avec ses faits
 * officiels (AssociationsPageFicheIdentite), jamais cachée à Google.
 * Avant : indexable seulement avec du contenu public ou une description de
 * 80 caractères, et RNA/SIRET (règles du 16/07 et du 18/10/2026).
 */
export function isOrgIndexable(_org: IndexableOrgShape): boolean {
  // Karim 26/10/2026 : toute fiche d'association publique est indexée, même
  // vide ou « non configurée » — on l'enrichit, on ne la cache pas. Les
  // fiches de TEST restent écartées par isTestOrgSlug. Fonction gardée pour
  // les hubs géographiques et le plan du site (une seule règle partagée).
  return true;
}

/**
 * Slugs d'organisations de TEST à ne jamais exposer (sitemap, hubs).
 * L'idéal serait un flag `is_test` côté Directus ; en attendant, filet sur le slug.
 */
export function isTestOrgSlug(slug: string): boolean {
  return /(^|[-_])(test|delete-me|demo|sample)([-_]|$)/i.test(slug);
}

/**
 * Un hub géographique est indexable s'il liste au moins `min` items ET qu'au
 * moins un de ces items est lui-même digne d'index. Pour les hubs cagnottes /
 * événements / adhésions, tous les items listés sont publics et actifs par
 * construction → `hasIndexableItem` vaut true dès qu'il y a un item.
 */
export function isGeoHubIndexable(
  itemCount: number,
  min: number,
  hasIndexableItem = true,
): boolean {
  return itemCount >= min && hasIndexableItem;
}
